import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, ArrowLeft, Filter } from 'lucide-react';
import { CHANGELOG_ENTRIES, CATEGORY_STYLES, CATEGORY_ORDER } from './changelogData';

/**
 * ChangelogPage
 * ---------------------------------------------------------------------------
 * Timeline of every user-facing change we've shipped. Newest first. Reads from
 * the flat CHANGELOG_ENTRIES array in `changelogData.js` — no backend, no DB.
 * When we ship a new change, we append one entry to that file.
 *
 * Filters:
 *   - Category chips (toggle multiple)
 *   - Free-text search across title, summary, and detail bullets
 */
const ChangelogPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategories, setActiveCategories] = useState(new Set()); // empty = show all

  const toggleCategory = (cat) => {
    setActiveCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const filteredEntries = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return CHANGELOG_ENTRIES.filter((entry) => {
      if (activeCategories.size > 0 && !activeCategories.has(entry.category)) return false;
      if (!q) return true;
      const haystack = [
        entry.title,
        entry.summary,
        entry.where || '',
        ...(entry.details || []),
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [searchTerm, activeCategories]);

  // Group entries by month for a nicer visual rhythm (July 2026, June 2026, …)
  const grouped = useMemo(() => {
    const buckets = new Map();
    for (const e of filteredEntries) {
      const [y, m] = e.date.split('-');
      const key = `${y}-${m}`;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(e);
    }
    return Array.from(buckets.entries()).map(([key, entries]) => {
      const [y, m] = key.split('-');
      const label = new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
      return { key, label, entries };
    });
  }, [filteredEntries]);

  return (
    <div className="changelog-page" style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 1rem' }} data-testid="changelog-page">
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <Button
          variant="ghost"
          onClick={() => navigate('/help')}
          style={{ marginBottom: '1rem', padding: '0.5rem' }}
          data-testid="changelog-back-to-help"
        >
          <ArrowLeft size={18} style={{ marginRight: '0.5rem' }} />
          Back to Help
        </Button>

        <h1 style={{ fontSize: '2rem', fontWeight: 700, color: '#1e40af', marginBottom: '0.5rem' }}>
          📜 Changelog
        </h1>
        <p style={{ color: '#64748b', margin: 0 }}>
          Every improvement, feature and fix we&apos;ve shipped. Newest first.
        </p>
      </div>

      {/* Search + filters */}
      <Card style={{ marginBottom: '1.5rem' }}>
        <CardContent style={{ padding: '1rem', display: 'grid', gap: '0.85rem' }}>
          <div style={{ position: 'relative' }}>
            <Search
              size={18}
              style={{
                position: 'absolute',
                left: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#94a3b8',
              }}
            />
            <Input
              type="text"
              placeholder="Search changes…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: '2.5rem' }}
              data-testid="changelog-search-input"
            />
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8rem', color: '#64748b', marginRight: '0.25rem' }}>
              <Filter size={14} /> Filter:
            </span>
            {CATEGORY_ORDER.map((cat) => {
              const active = activeCategories.has(cat);
              const style = CATEGORY_STYLES[cat] || { bg: '#f1f5f9', fg: '#334155', border: '#cbd5e1' };
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => toggleCategory(cat)}
                  data-testid={`changelog-filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    padding: '0.35rem 0.7rem',
                    borderRadius: '999px',
                    border: `1px solid ${style.border}`,
                    backgroundColor: active ? style.fg : style.bg,
                    color: active ? '#fff' : style.fg,
                    cursor: 'pointer',
                    transition: 'all 120ms',
                  }}
                >
                  {cat}
                </button>
              );
            })}
            {activeCategories.size > 0 && (
              <button
                type="button"
                onClick={() => setActiveCategories(new Set())}
                data-testid="changelog-clear-filters"
                style={{ fontSize: '0.75rem', color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', marginLeft: '0.25rem' }}
              >
                Clear
              </button>
            )}
          </div>

          <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
            Showing {filteredEntries.length} of {CHANGELOG_ENTRIES.length} entries
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      {grouped.length === 0 ? (
        <Card>
          <CardContent style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
            No changes match your filters.
          </CardContent>
        </Card>
      ) : (
        grouped.map((month) => (
          <div key={month.key} style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#334155', margin: '0 0 0.75rem 0.25rem', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
              {month.label}
            </h2>
            <div style={{ display: 'grid', gap: '0.85rem' }}>
              {month.entries.map((entry, idx) => (
                <ChangelogEntry key={`${entry.date}-${idx}`} entry={entry} />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

const ChangelogEntry = ({ entry }) => {
  const style = CATEGORY_STYLES[entry.category] || { bg: '#f1f5f9', fg: '#334155', border: '#cbd5e1' };
  const dateFormatted = new Date(entry.date + 'T00:00:00').toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
  });
  return (
    <Card style={{ borderLeft: `4px solid ${style.fg}` }} data-testid="changelog-entry">
      <CardContent style={{ padding: '1rem 1.25rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
            <span
              style={{
                fontSize: '0.7rem',
                fontWeight: 700,
                padding: '0.2rem 0.55rem',
                borderRadius: '999px',
                backgroundColor: style.bg,
                color: style.fg,
                border: `1px solid ${style.border}`,
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
              }}
            >
              {entry.category}
            </span>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#0f172a' }}>{entry.title}</h3>
          </div>
          <span style={{ fontSize: '0.75rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>{dateFormatted}</span>
        </div>

        <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: '#334155', lineHeight: 1.5 }}>
          {entry.summary}
        </p>

        {entry.where && (
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: entry.details?.length ? '0.5rem' : 0 }}>
            <strong>Where:</strong> {entry.where}
          </div>
        )}

        {entry.details && entry.details.length > 0 && (
          <ul style={{ margin: '0.4rem 0 0 1.1rem', padding: 0, fontSize: '0.8rem', color: '#475569', lineHeight: 1.55 }}>
            {entry.details.map((d, i) => (
              <li key={i} style={{ marginBottom: '0.2rem' }}>{d}</li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default ChangelogPage;
export { ChangelogPage };
